// Core services shown in the navigation, footer, homepage and /services.
//
// `path` is the URL agreed in docs/REPOSITIONING-PLAN.md §3.2.
// `live` is true when that route exists; otherwise links fall back to
// `anchor` (a homepage section) so nothing lands on the landing-page
// catch-all.
//
// Entries with `page` are rendered by pages/services/ServicePage.jsx.
// Website development and Google Ads keep their own existing pages.
// Copy must stay factual: no guaranteed results, invented metrics or
// fixed packages. SEO & GEO copy must not promise rankings, AI citations
// or inclusion in AI-generated answers.
//
// Optional `page` sections:
//   `pillars`  two-column explainer shown after the hero (SEO & GEO)
//   `feature`  a highlighted capability with its own anchor (AI chatbots)
//   `heroLink` a secondary link in the hero, usually to the feature anchor
//   `schema`   { serviceType } enables Service JSON-LD built from the visible
//              page content (pages/services/serviceSchema.js). Only describe
//              what the page itself states; no ratings, prices or FAQ.
// Optional `highlight` adds a secondary link to the service card.

export const CHATBOT_ANCHOR = "ai-chatbots"
export const CHATBOT_PATH = `/business-integrations#${CHATBOT_ANCHOR}`
export const CHATBOT_LABEL = "AI Chatbots & Intelligent Customer Engagement"
export const SEO_GEO_LABEL = "SEO & GEO — Search and AI Visibility"

export const SERVICES = [
  {
    key: "digital-strategy",
    navLabel: "Digital Strategy",
    title: "Digital Strategy & Growth",
    path: "/digital-strategy",
    live: true,
    anchor: "/#services",
    icon: "Compass",
    summary:
      "A clear view of where your website, search presence and marketing spend stand today, and a prioritised plan for what to improve next.",
    cta: "Plan your next step",
    page: {
      seoTitle: "Digital Strategy & Growth Adelaide | 2merative",
      seoDescription:
        "Digital strategy for established Adelaide businesses. A clear review of your website, search visibility and marketing spend, with a prioritised plan for growth.",
      heroHeading: "A clear plan for your digital growth",
      heroLead:
        "Before investing in a new website or more advertising, understand what is working, what is not and where the biggest opportunities are. We review your digital presence as a whole and give you a prioritised plan.",
      challengesHeading: "When strategy should come first",
      challenges: [
        "You are unsure whether to fix, rebuild or advertise",
        "Marketing spend is growing but enquiries are not",
        "Your website, Google profile and campaigns were set up at different times by different people",
        "You do not have a clear picture of where enquiries actually come from",
      ],
      includedHeading: "What a strategy engagement covers",
      included: [
        {
          title: "Website review",
          text: "Structure, messaging, performance and the path from first visit to enquiry.",
        },
        {
          title: "Search visibility",
          text: "How you appear in Google Search, Maps and AI-powered search experiences compared with competitors.",
        },
        {
          title: "Tracking and data",
          text: "Whether GA4, Search Console and conversion tracking are set up and reliable.",
        },
        {
          title: "Marketing spend",
          text: "Where your current advertising budget goes and what it is returning.",
        },
        {
          title: "Systems and workflow",
          text: "Where enquiries, bookings and customer data are still handled manually, and where automation or an AI chatbot could help.",
        },
        {
          title: "Prioritised roadmap",
          text: "A practical sequence of improvements, with the highest-impact work first.",
        },
      ],
      process: [
        { title: "Discovery", text: "We talk through your business, goals, customers and current set-up." },
        { title: "Review", text: "We assess your website, search presence, tracking and campaigns." },
        { title: "Recommendations", text: "You receive clear priorities and the reasoning behind them." },
        { title: "Implementation", text: "Implement with us, with your own team, or a mix of both." },
      ],
      related: [
        { to: "/online-presence-audit", label: "Online presence audit" },
        { to: "/conversion-audit", label: "Conversion audit" },
        { to: "/fix-vs-rebuild", label: "Fix or rebuild your website?" },
        { to: "/not-sure-what-to-fix-first", label: "Not sure what to fix first?" },
      ],
    },
  },
  {
    key: "website-development",
    navLabel: "Website Development",
    title: "Premium Website Design & Development",
    path: "/web-design-adelaide",
    live: true,
    anchor: "/#services",
    icon: "Monitor",
    summary:
      "Custom-built websites that represent your business properly, load fast on every device and make it easy for the right customers to enquire.",
    cta: "Explore website development",
  },
  {
    key: "seo",
    navLabel: "SEO & GEO",
    title: SEO_GEO_LABEL,
    path: "/seo-adelaide",
    live: true,
    anchor: "/#search-visibility",
    icon: "Search",
    summary:
      "Strengthen your presence across Google search and AI-powered discovery through technical optimisation, authoritative content and a clear digital footprint.",
    cta: "Improve your visibility",
    page: {
      seoTitle: "SEO & GEO Adelaide | Search and AI Visibility | 2merative",
      schema: { serviceType: "Search engine optimisation and generative engine optimisation" },
      seoDescription:
        "SEO and GEO for established Adelaide businesses. Improve your visibility in Google search and how clearly AI-powered search tools understand your business.",
      heroHeading: "Be found, and clearly understood, wherever customers search",
      heroLead:
        "Customers now look for businesses in Google Search and Maps, and increasingly in AI-powered search and answer tools. We strengthen the foundations both rely on, so your business is easier to find and easier to understand. No new website required.",
      pillars: {
        eyebrow: "Two complementary disciplines",
        heading: "SEO and GEO work together",
        lead: "GEO does not replace SEO. Both depend on a fast, well-structured website with clear, trustworthy information, so most of the work strengthens your visibility in both.",
        items: [
          {
            label: "SEO",
            title: "Search engine optimisation",
            text: "Improving how your website appears in traditional search results through technical optimisation, relevant content, clear site structure, local search and ongoing refinement.",
            points: ["Google Search and Maps", "Local and service-area searches", "Rankings, traffic and enquiries"],
          },
          {
            label: "GEO",
            title: "Generative engine optimisation",
            text: "Improving how clearly your business and services can be understood, discovered and represented in AI-powered search and answer experiences.",
            points: [
              "Google's AI search experiences",
              "ChatGPT and other AI assistants",
              "Accurate, consistent business information",
            ],
          },
        ],
      },
      challengesHeading: "Signs your search and AI visibility needs attention",
      challenges: [
        "Competitors appear above you for your core services",
        "Your Google Business Profile is incomplete or not generating calls",
        "Key service pages do not rank, or do not exist yet",
        "Your services, locations or expertise are not explained clearly enough for people or AI tools to summarise accurately",
        "You have no reliable data on search traffic or the enquiries it produces",
      ],
      includedHeading: "What SEO & GEO with 2merative includes",
      included: [
        {
          title: "Technical SEO and performance",
          text: "Crawlability, indexing, site speed and metadata, so your pages can be found and load quickly.",
        },
        {
          title: "Site structure and internal linking",
          text: "Clear architecture and navigation that help people, search engines and AI systems reach your key pages.",
        },
        {
          title: "Service and location content",
          text: "Pages built around search intent, explaining what you offer and where, in plain, accessible language.",
        },
        {
          title: "Customer questions answered",
          text: "Clear, informative answers to the questions customers ask before they enquire.",
        },
        {
          title: "Local search and Google Business Profile",
          text: "Categories, services and business details kept consistent across your website, profile and directories.",
        },
        {
          title: "Expertise and trust signals",
          text: "Experience, credentials, reviews and supporting evidence presented clearly and credibly.",
        },
        {
          title: "Structured data",
          text: "Schema markup where appropriate, to describe your business and services in a machine-readable way.",
        },
        {
          title: "Measurement and reporting",
          text: "GA4 and Search Console set up properly, with regular reporting and clear recommendations.",
        },
      ],
      process: [
        { title: "Audit", text: "We review your site, search presence, business information and competitors." },
        { title: "Prioritise", text: "We agree the changes most likely to make a difference first." },
        { title: "Implement", text: "Technical fixes, content improvements and profile optimisation." },
        { title: "Monitor", text: "We track progress and refine the approach over time." },
      ],
      note:
        "SEO and GEO take time, and no one can honestly guarantee rankings, AI citations or inclusion in AI-generated answers. Search engines and AI platforms decide what they show, and structured data alone does not create visibility. We focus on the fundamentals that make your business easier to find and understand, and report clearly on progress.",
      related: [
        { to: "/google-business-profile-setup", label: "Google Business Profile set-up" },
        { to: "/local-seo-starter-pack", label: "Local SEO starter pack" },
        { to: "/website-not-showing-on-google", label: "Website not showing on Google" },
        { to: "/competitors-outrank-me", label: "Competitors outranking you" },
        { to: "/search-console-setup", label: "Google Search Console set-up" },
        { to: "/ga4-setup", label: "GA4 set-up" },
      ],
    },
  },
  {
    key: "google-ads",
    navLabel: "Google Ads",
    title: "Google Ads & Lead Generation",
    path: "/google-ads-adelaide",
    live: true,
    anchor: "/#search-visibility",
    icon: "Megaphone",
    summary:
      "Focused campaigns with proper conversion tracking, so your advertising budget is spent on searches that lead to genuine enquiries.",
    cta: "Explore Google Ads",
  },
  {
    key: "business-integrations",
    navLabel: "Integrations & AI Chatbots",
    title: "Business Integrations & Automation",
    path: "/business-integrations",
    live: true,
    anchor: "/#integrations",
    icon: "Workflow",
    summary:
      "Connect your website to your CRM, booking, e-commerce and administration tools to reduce double handling and keep information flowing.",
    cta: "Connect your systems",
    highlight: { to: CHATBOT_PATH, label: CHATBOT_LABEL },
    page: {
      seoTitle: "Business Integrations, Automation & AI Chatbots | 2merative",
      schema: { serviceType: "Business systems integration, automation and AI chatbot development" },
      seoDescription:
        "Connect your website to CRM, booking and admin systems, and add AI chatbots that answer customer questions, qualify enquiries and hand over to your team.",
      heroHeading: "Connect your website to the way your business runs",
      heroLead:
        "Custom development that links your website with the tools your business already relies on, and AI chatbots that help customers find answers and take the next step. Enquiries, bookings and customer information reach the right place without double handling.",
      heroLink: { to: `#${CHATBOT_ANCHOR}`, label: `Explore ${CHATBOT_LABEL}` },
      challengesHeading: "Common signs of disconnected systems",
      challenges: [
        "Enquiries are copied from email into a CRM by hand",
        "Bookings and availability are managed in more than one place",
        "Product or service information is updated in several systems",
        "Your team answers the same customer questions every day",
        "Follow-ups fall through the gaps between tools",
      ],
      includedHeading: "What we can connect",
      included: [
        {
          title: "CRM systems",
          text: "Website enquiries routed straight into the CRM your team already uses.",
        },
        {
          title: "Booking platforms",
          text: "Booking and scheduling tools connected to your website and calendar.",
        },
        {
          title: "E-commerce",
          text: "Online stores and product information connected to your website and operations.",
        },
        {
          title: "Enquiry workflows",
          text: "Forms that reach the right person or system, with confirmations and follow-up.",
        },
        {
          title: "Administration tools",
          text: "Databases such as Airtable and internal tools built around your process.",
        },
        {
          title: "External APIs",
          text: "Third-party services and APIs connected where they genuinely add value.",
        },
      ],
      feature: {
        id: CHATBOT_ANCHOR,
        eyebrow: "AI chatbots",
        heading: CHATBOT_LABEL,
        lead: "A well-built AI chatbot is a practical business tool. It helps visitors find the right information quickly, guides them towards the services that suit them and turns more of your existing website traffic into genuine enquiries.",
        capabilitiesHeading: "What an AI chatbot can do for your website",
        capabilities: [
          {
            icon: "MessageSquare",
            title: "Answer customer questions",
            text: "An AI-powered website assistant that responds to common questions clearly and consistently.",
          },
          {
            icon: "Compass",
            title: "Guide visitors",
            text: "Intelligent navigation that points people to the relevant products, services and information.",
          },
          {
            icon: "BookOpen",
            title: "Use approved content",
            text: "Answers drawn from your own approved business content, not from guesswork.",
          },
          {
            icon: "UserCheck",
            title: "Capture and qualify leads",
            text: "Collect enquiry details and ask the right questions before the conversation reaches your team.",
          },
          {
            icon: "CalendarCheck",
            title: "Direct the next step",
            text: "Lead customers towards bookings, contact forms and sales opportunities.",
          },
          {
            icon: "Plug",
            title: "Connect to your systems",
            text: "Integration with CRM, booking platforms and existing workflows, where those platforms support it.",
          },
          {
            icon: "Clock",
            title: "Help outside business hours",
            text: "Reduce repetitive enquiries and give customers useful answers when your team is offline.",
          },
          {
            icon: "Users",
            title: "Hand over to a person",
            text: "Pass the conversation to your team when a customer needs personal assistance.",
          },
        ],
        benefitsHeading: "The commercial value",
        benefits: [
          "Improved customer experience",
          "Faster access to relevant information",
          "More opportunities to convert website visitors",
          "Reduced repetitive administration",
          "Better lead qualification",
          "Scalable customer engagement",
        ],
        note: "Every chatbot is scoped to your business. Integrations depend on what your CRM, booking and other platforms support, and are quoted separately rather than included by default. We are upfront about limitations, and a chatbot works best alongside your team rather than in place of it.",
        cta: { to: "/contact?intent=chatbot", label: "Discuss an AI Chatbot" },
      },
      process: [
        { title: "Map the workflow", text: "We document how information moves through your business today." },
        { title: "Assess platforms", text: "We confirm what each platform supports, including any limitations." },
        { title: "Build and test", text: "We build the connections and test them with real scenarios." },
        { title: "Support", text: "We monitor and adjust as your tools and processes change." },
      ],
      note:
        "What is possible depends on the platforms involved. We assess this during scoping and are upfront about any technical or third-party limitations before work begins.",
      related: [{ to: "/contact-form-not-working", label: "Contact form not working" }],
    },
  },
  {
    key: "digital-support",
    navLabel: "Ongoing Support",
    title: "Ongoing Digital Support",
    path: "/digital-support",
    live: true,
    anchor: "/#support",
    icon: "LifeBuoy",
    summary:
      "Continuous improvement after launch: updates, technical support, SEO & GEO monitoring, campaign management and practical recommendations.",
    cta: "Talk about ongoing support",
    page: {
      seoTitle: "Ongoing Digital Support & Optimisation | 2merative",
      seoDescription:
        "Ongoing website, SEO & GEO and Google Ads support for established businesses. Updates, technical support, monitoring and recommendations, scoped to your needs.",
      heroHeading: "Ongoing support that keeps improving results",
      heroLead:
        "Your website and campaigns should keep improving after launch. We provide ongoing support from a team that already knows your business, scoped around what you actually need.",
      challengesHeading: "Why ongoing support matters",
      challenges: [
        "Websites drift out of date as the business changes",
        "Tracking can break quietly after updates",
        "Search and AI visibility, and campaigns, need regular attention",
        "Small technical issues become costly when no one is watching",
      ],
      includedHeading: "What ongoing support can include",
      included: [
        {
          title: "Website updates",
          text: "Content, page and feature changes as your business evolves, including keeping AI chatbot content current.",
        },
        {
          title: "Technical support",
          text: "A direct point of contact when something needs fixing.",
        },
        {
          title: "SEO & GEO monitoring",
          text: "Ongoing optimisation based on how your search and AI visibility is tracking.",
        },
        {
          title: "Campaign management",
          text: "Google Ads reviewed and refined using real performance data.",
        },
        {
          title: "Health checks",
          text: "Speed, forms and tracking checked so problems are caught early.",
        },
        {
          title: "Reporting and recommendations",
          text: "Clear updates on what changed, what it achieved and what to do next.",
        },
      ],
      process: [
        { title: "Agree priorities", text: "We confirm the scope, priorities and reporting with you." },
        { title: "Regular work", text: "Improvements and support are delivered on an agreed rhythm." },
        { title: "Review", text: "We review results together and explain what they mean." },
        { title: "Adjust", text: "Priorities shift as your business and the data change." },
      ],
      related: [
        { to: "/website-care-plans", label: "Website care plans" },
        { to: "/website-speed-fix", label: "Website speed fix" },
        { to: "/tracking-broken-after-update", label: "Tracking broken after updates" },
      ],
    },
  },
]

export function serviceHref(service) {
  return service.live ? service.path : service.anchor
}

export function getService(key) {
  return SERVICES.find((service) => service.key === key)
}

export const SERVICES_HUB = "/services"
export const CONTACT_PROJECT = "/contact?intent=project"
export const CONTACT_QUOTE = "/contact?intent=quote"
